/*
 * Amana Labs — pre-launch storefront interactions
 * Vanilla JS, progressively enhanced and safe for the Shopify theme editor.
 */
(function () {
  "use strict";

  if (typeof window === "undefined" || typeof document === "undefined") return;

  var RUNTIME_KEY = "__AMANA_PRELAUNCH_RUNTIME__";
  var previousRuntime = window[RUNTIME_KEY];
  if (previousRuntime && typeof previousRuntime.destroy === "function") {
    previousRuntime.destroy();
  }

  var html = document.documentElement;
  html.classList.add("js", "amana-js");

  var uid = 0;
  var FOCUSABLE_SELECTOR = [
    "a[href]",
    "area[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "details > summary:first-of-type",
    "[tabindex]:not([tabindex='-1'])",
    "[contenteditable='true']"
  ].join(",");

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function queryAll(scope, selector) {
    return toArray((scope || document).querySelectorAll(selector));
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function ensureId(element, prefix) {
    if (!element.id) {
      uid += 1;
      element.id = (prefix || "amana-control") + "-" + uid;
    }
    return element.id;
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }

  function getHashTarget(rawHash) {
    if (!rawHash || rawHash === "#") return null;
    var id = rawHash.charAt(0) === "#" ? rawHash.slice(1) : rawHash;
    try {
      id = decodeURIComponent(id);
    } catch (_error) {
      // Keep the literal hash when it is not valid percent-encoded text.
    }
    return document.getElementById(id);
  }

  function isVisible(element) {
    if (!element || element.hidden) return false;
    var style = window.getComputedStyle(element);
    if (style.visibility === "hidden" || style.display === "none") return false;
    return element.getClientRects().length > 0;
  }

  function getFocusable(container) {
    return queryAll(container, FOCUSABLE_SELECTOR).filter(function (element) {
      return isVisible(element) && element.getAttribute("aria-hidden") !== "true";
    });
  }

  function addListener(cleanups, target, type, listener, options) {
    if (!target || typeof target.addEventListener !== "function") return;
    target.addEventListener(type, listener, options);
    cleanups.push(function () {
      target.removeEventListener(type, listener, options);
    });
  }

  function addMediaListener(cleanups, mediaQuery, listener) {
    if (!mediaQuery) return;
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", listener);
      cleanups.push(function () {
        mediaQuery.removeEventListener("change", listener);
      });
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(listener);
      cleanups.push(function () {
        mediaQuery.removeListener(listener);
      });
    }
  }

  function mergeDescribedBy(element, id, add) {
    if (!element || !id) return;
    var ids = (element.getAttribute("aria-describedby") || "")
      .split(/\s+/)
      .filter(Boolean);
    var index = ids.indexOf(id);
    if (add && index === -1) ids.push(id);
    if (!add && index !== -1) ids.splice(index, 1);
    if (ids.length) element.setAttribute("aria-describedby", ids.join(" "));
    else element.removeAttribute("aria-describedby");
  }

  function isModifiedClick(event) {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function sanitiseTag(value) {
    return String(value || "")
      .replace(/[,\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80);
  }

  function FocusTrap(container, restoreTarget, onEscape) {
    this.container = container;
    this.restoreTarget = restoreTarget;
    this.onEscape = onEscape;
    this.active = false;
    this.addedTabindex = false;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  FocusTrap.prototype.activate = function (preferredFocus) {
    if (this.active) return;
    this.active = true;
    document.addEventListener("keydown", this.handleKeydown, true);

    var focusTarget = preferredFocus;
    if (!focusTarget || !isVisible(focusTarget)) {
      focusTarget = getFocusable(this.container)[0];
    }
    if (!focusTarget) {
      if (!this.container.hasAttribute("tabindex")) {
        this.container.setAttribute("tabindex", "-1");
        this.addedTabindex = true;
      }
      focusTarget = this.container;
    }

    window.requestAnimationFrame(function () {
      if (focusTarget && typeof focusTarget.focus === "function") {
        focusTarget.focus({ preventScroll: true });
      }
    });
  };

  FocusTrap.prototype.handleKeydown = function (event) {
    if (!this.active) return;
    if (event.key === "Escape") {
      event.preventDefault();
      if (typeof this.onEscape === "function") this.onEscape();
      return;
    }
    if (event.key !== "Tab") return;

    var focusable = getFocusable(this.container);
    if (!focusable.length) {
      event.preventDefault();
      this.container.focus({ preventScroll: true });
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var current = document.activeElement;
    if (event.shiftKey && (current === first || !this.container.contains(current))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  };

  FocusTrap.prototype.deactivate = function (restoreFocus) {
    if (!this.active) return;
    this.active = false;
    document.removeEventListener("keydown", this.handleKeydown, true);
    if (this.addedTabindex) {
      this.container.removeAttribute("tabindex");
      this.addedTabindex = false;
    }
    if (
      restoreFocus !== false &&
      this.restoreTarget &&
      document.contains(this.restoreTarget) &&
      typeof this.restoreTarget.focus === "function"
    ) {
      this.restoreTarget.focus({ preventScroll: true });
    }
  };

  function AmanaController() {
    this.cleanups = [];
    this.revealObserver = null;
    this.resizeObserver = null;
    this.lenis = null;
    this.scrollFrame = 0;
    this.measureFrame = 0;
    this.destroyed = false;
    this.activeOverlay = null;
    this.nav = null;
    this.dialogStates = [];
    this.accordions = [];
    this.tabGroups = [];
    this.signupStates = [];
    this.railMetrics = [];
    this.methodMetrics = [];
    this.haloMetrics = [];

    this.reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.finePointerQuery = window.matchMedia("(min-width: 990px) and (pointer: fine)");
    this.reduced = this.reducedQuery.matches;
    this.saveData = Boolean(navigator.connection && navigator.connection.saveData);
    this.hardwareConcurrency = Number(navigator.hardwareConcurrency || 0);
    this.smoothScrollEnabled = Boolean(
      document.body && document.body.getAttribute("data-amana-smooth-scroll") === "true"
    );
    this.motionIntensity = document.body
      ? document.body.getAttribute("data-amana-motion-intensity") || "balanced"
      : "balanced";
    this.motionScale = this.motionIntensity === "subtle"
      ? 0.65
      : this.motionIntensity === "expressive"
        ? 1.2
        : 1;
    this.motionEnabled =
      !this.reduced &&
      !this.saveData &&
      (this.hardwareConcurrency === 0 || this.hardwareConcurrency > 4);
    this.ambientMotionEnabled = Boolean(
      this.motionEnabled &&
      document.body &&
      document.body.getAttribute("data-amana-ambient-motion") !== "false"
    );

    html.classList.toggle("amana-reduced-motion", this.reduced);
    html.classList.toggle("amana-ambient-motion-off", !this.ambientMotionEnabled);

    this.scheduleScrollUpdate = this.scheduleScrollUpdate.bind(this);
    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);

    this.setupNav();
    this.setupAccordions();
    this.setupTabs();
    this.setupDialogs();
    this.setupSignup();
    this.setupReveal();
    this.setupLenis();
    this.setupAnchors();
    this.setupScrollLinkedElements();
    this.setupLifecycleObservers();
    this.scheduleMeasure();
  }

  AmanaController.prototype.closeActiveOverlay = function (restoreFocus) {
    var active = this.activeOverlay;
    if (!active || typeof active.close !== "function") return;
    active.close(restoreFocus);
  };

  AmanaController.prototype.setActiveOverlay = function (overlay) {
    this.activeOverlay = overlay;
    html.classList.toggle("amana-overlay-open", Boolean(overlay));
    if (overlay && this.lenis && typeof this.lenis.stop === "function") {
      this.lenis.stop();
    }
  };

  AmanaController.prototype.clearActiveOverlay = function (overlay) {
    if (this.activeOverlay !== overlay) return;
    this.activeOverlay = null;
    html.classList.remove("amana-overlay-open");
    if (!document.hidden && this.lenis && typeof this.lenis.start === "function") {
      this.lenis.start();
    }
  };

  AmanaController.prototype.setupNav = function () {
    var controller = this;
    var details = document.querySelector("[data-amana-mobile-menu]");
    var toggle = document.querySelector("[data-amana-nav-toggle]") || (details && details.querySelector("summary"));
    var panel = document.querySelector("[data-amana-nav-panel]") || (details && details.querySelector(".amana-header__menu-panel"));
    if (!toggle || !panel) return;

    var panelId = ensureId(panel, "amana-navigation");
    toggle.setAttribute("aria-controls", panelId);
    toggle.setAttribute("aria-expanded", "false");
    panel.setAttribute("aria-hidden", "true");
    if (details) details.open = false;
    else panel.hidden = true;

    var navState = {
      toggle: toggle,
      panel: panel,
      open: false,
      trap: null,
      overlay: null
    };
    this.nav = navState;

    function closeNav(restoreFocus) {
      if (!navState.open) return;
      navState.open = false;
      toggle.setAttribute("aria-expanded", "false");
      panel.setAttribute("aria-hidden", "true");
      panel.classList.remove("is-open");
      panel.removeAttribute("data-open");
      if (details) details.open = false;
      else panel.hidden = true;
      html.classList.remove("amana-nav-open");
      if (navState.trap) navState.trap.deactivate(restoreFocus);
      navState.trap = null;
      controller.clearActiveOverlay(navState.overlay);
      navState.overlay = null;
    }

    function openNav() {
      if (navState.open) return;
      controller.closeActiveOverlay(false);
      navState.open = true;
      toggle.setAttribute("aria-expanded", "true");
      if (details) details.open = true;
      else panel.hidden = false;
      panel.setAttribute("aria-hidden", "false");
      panel.classList.add("is-open");
      panel.setAttribute("data-open", "");
      html.classList.add("amana-nav-open");
      navState.trap = new FocusTrap(panel, toggle, function () {
        closeNav(true);
      });
      navState.overlay = {
        close: closeNav
      };
      controller.setActiveOverlay(navState.overlay);
      navState.trap.activate(panel.querySelector("[autofocus]"));
    }

    navState.openMenu = openNav;
    navState.closeMenu = closeNav;

    addListener(this.cleanups, toggle, "click", function (event) {
      event.preventDefault();
      if (navState.open) closeNav(true);
      else openNav();
    });

    addListener(this.cleanups, document, "pointerdown", function (event) {
      if (!navState.open) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      closeNav(false);
    });

    queryAll(panel, "a[href]").forEach(function (link) {
      addListener(controller.cleanups, link, "click", function () {
        closeNav(false);
      });
    });

    addMediaListener(this.cleanups, window.matchMedia("(min-width: 901px)"), function (event) {
      if (event.matches) closeNav(false);
    });
  };

  AmanaController.prototype.setupAccordions = function () {
    var controller = this;
    queryAll(document, "[data-amana-method]").forEach(function (method) {
      var items = [];
      queryAll(method, "[data-amana-accordion-trigger]").forEach(function (trigger, index) {
        var panel = null;
        var controlledId = trigger.getAttribute("aria-controls");
        if (controlledId) panel = document.getElementById(controlledId);
        if (!panel) {
          var item = trigger.closest("[data-amana-method-step], li, article, div");
          panel = item && item.querySelector("[data-amana-accordion-panel]");
        }
        if (!panel) return;

        var triggerId = ensureId(trigger, "amana-accordion-trigger");
        var panelId = ensureId(panel, "amana-accordion-panel");
        trigger.setAttribute("aria-controls", panelId);
        panel.setAttribute("aria-labelledby", triggerId);
        panel.setAttribute("role", panel.getAttribute("role") || "region");

        var expanded = trigger.getAttribute("aria-expanded") === "true" && !panel.hidden;
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
        panel.hidden = !expanded;
        var itemElement = trigger.closest("[data-amana-method-step]") || trigger.parentElement;
        if (itemElement) itemElement.classList.toggle("is-open", expanded);

        items.push({
          trigger: trigger,
          panel: panel,
          item: itemElement,
          index: index
        });
      });

      function setItem(item, open) {
        item.trigger.setAttribute("aria-expanded", open ? "true" : "false");
        item.panel.hidden = !open;
        item.panel.setAttribute("aria-hidden", open ? "false" : "true");
        if (item.item) item.item.classList.toggle("is-open", open);
        var icon = item.trigger.querySelector("[data-amana-accordion-icon]");
        if (icon) icon.textContent = open ? "−" : "+";
      }

      function openItem(targetItem) {
        items.forEach(function (item) {
          setItem(item, item === targetItem);
        });
        controller.scheduleMeasure();
      }

      items.forEach(function (item) {
        addListener(controller.cleanups, item.trigger, "click", function () {
          var willOpen = item.trigger.getAttribute("aria-expanded") !== "true";
          if (willOpen) openItem(item);
          else {
            setItem(item, false);
            controller.scheduleMeasure();
          }
        });
      });

      controller.accordions.push({
        method: method,
        items: items,
        openItem: openItem
      });
    });
  };

  AmanaController.prototype.setupTabs = function () {
    var controller = this;
    queryAll(document, "[data-amana-tabs]").forEach(function (group) {
      var tabs = queryAll(group, "[role='tab']");
      var panels = queryAll(group, "[role='tabpanel'], [data-amana-tab-panel]");
      if (!tabs.length || !panels.length) return;

      panels.forEach(function (panel) {
        panel.setAttribute("role", "tabpanel");
      });

      function panelForTab(tab, index) {
        var id = tab.getAttribute("aria-controls") || tab.getAttribute("data-tab-target");
        if (id) return getHashTarget(id.charAt(0) === "#" ? id : "#" + id);
        var href = tab.getAttribute("href");
        if (href && href.charAt(0) === "#") return getHashTarget(href);
        return panels[index] || null;
      }

      var pairs = tabs.map(function (tab, index) {
        var panel = panelForTab(tab, index);
        if (!panel) return null;
        var tabId = ensureId(tab, "amana-tab");
        var panelId = ensureId(panel, "amana-tab-panel");
        tab.setAttribute("aria-controls", panelId);
        panel.setAttribute("aria-labelledby", tabId);
        return { tab: tab, panel: panel };
      }).filter(Boolean);
      if (!pairs.length) return;

      function select(index, focus) {
        index = (index + pairs.length) % pairs.length;
        pairs.forEach(function (pair, pairIndex) {
          var selected = pairIndex === index;
          pair.tab.setAttribute("aria-selected", selected ? "true" : "false");
          pair.tab.tabIndex = selected ? 0 : -1;
          pair.tab.classList.toggle("is-active", selected);
          pair.panel.hidden = !selected;
          pair.panel.classList.toggle("is-active", selected);
          pair.panel.setAttribute("aria-hidden", selected ? "false" : "true");
        });
        if (focus) pairs[index].tab.focus();
        controller.scheduleMeasure();
      }

      var initialIndex = pairs.findIndex(function (pair) {
        return pair.tab.getAttribute("aria-selected") === "true";
      });
      select(initialIndex >= 0 ? initialIndex : 0, false);

      pairs.forEach(function (pair, index) {
        addListener(controller.cleanups, pair.tab, "click", function (event) {
          event.preventDefault();
          select(index, false);
        });
        addListener(controller.cleanups, pair.tab, "keydown", function (event) {
          var orientation = group.getAttribute("aria-orientation") || "horizontal";
          var nextIndex = null;
          if (event.key === "Home") nextIndex = 0;
          else if (event.key === "End") nextIndex = pairs.length - 1;
          else if (
            (orientation === "vertical" && event.key === "ArrowDown") ||
            (orientation !== "vertical" && event.key === "ArrowRight")
          ) nextIndex = index + 1;
          else if (
            (orientation === "vertical" && event.key === "ArrowUp") ||
            (orientation !== "vertical" && event.key === "ArrowLeft")
          ) nextIndex = index - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          select(nextIndex, true);
        });
      });

      controller.tabGroups.push({ group: group, pairs: pairs, select: select });
    });
  };

  AmanaController.prototype.createDialogState = function (dialog, kind) {
    var controller = this;
    var state = {
      dialog: dialog,
      kind: kind,
      opener: null,
      trap: null,
      overlay: null,
      isOpen: false,
      restoreFocus: true
    };
    ensureId(dialog, "amana-" + kind + "-dialog");
    dialog.setAttribute("aria-hidden", dialog.hasAttribute("open") ? "false" : "true");
    if (!dialog.hasAttribute("open")) dialog.hidden = true;

    function finishClose() {
      if (!state.isOpen) return;
      state.isOpen = false;
      dialog.classList.remove("is-open");
      dialog.removeAttribute("data-open");
      dialog.setAttribute("aria-hidden", "true");
      if (!dialog.open) dialog.removeAttribute("open");
      dialog.hidden = true;
      if (state.trap) state.trap.deactivate(state.restoreFocus);
      state.trap = null;
      controller.clearActiveOverlay(state.overlay);
      state.overlay = null;
      controller.scheduleMeasure();
    }

    function closeDialog(restoreFocus) {
      if (!state.isOpen) return;
      state.restoreFocus = restoreFocus !== false;
      if (typeof dialog.close === "function" && dialog.open) {
        dialog.close();
      } else {
        finishClose();
      }
    }

    function openDialog(opener) {
      controller.closeActiveOverlay(false);
      state.opener = opener || document.activeElement;
      state.restoreFocus = true;
      state.isOpen = true;
      dialog.hidden = false;
      dialog.setAttribute("aria-hidden", "false");
      dialog.classList.add("is-open");
      dialog.setAttribute("data-open", "");

      if (typeof dialog.showModal === "function") {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }

      queryAll(dialog, "[data-reveal]").forEach(function (element) {
        element.classList.add("is-in");
      });

      state.trap = new FocusTrap(dialog, state.opener, function () {
        closeDialog(true);
      });
      state.overlay = { close: closeDialog };
      controller.setActiveOverlay(state.overlay);
      state.trap.activate(dialog.querySelector("[autofocus], [data-dialog-initial-focus]"));
      controller.scheduleMeasure();
    }

    state.openDialog = openDialog;
    state.closeDialog = closeDialog;
    state.finishClose = finishClose;

    queryAll(dialog, "[data-dialog-close], [data-amana-dialog-close]").forEach(function (button) {
      addListener(controller.cleanups, button, "click", function (event) {
        event.preventDefault();
        closeDialog(true);
      });
    });

    addListener(this.cleanups, dialog, "cancel", function (event) {
      event.preventDefault();
      closeDialog(true);
    });
    addListener(this.cleanups, dialog, "close", finishClose);
    addListener(this.cleanups, dialog, "click", function (event) {
      if (event.target !== dialog) return;
      var rect = dialog.getBoundingClientRect();
      var outsideSurface =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (outsideSurface) closeDialog(true);
    });

    this.dialogStates.push(state);
    return state;
  };

  AmanaController.prototype.ritualSourceValue = function (trigger, key) {
    var source = trigger.closest("[data-ritual-card], [data-amana-ritual], article, li") || trigger;
    var capitalized = key.charAt(0).toUpperCase() + key.slice(1);
    var candidates = [
      trigger.dataset[key],
      trigger.dataset["ritual" + capitalized],
      source.dataset && source.dataset[key],
      source.dataset && source.dataset["ritual" + capitalized]
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      if (candidates[i] !== undefined && candidates[i] !== "") return candidates[i];
    }

    var child = source.querySelector("[data-ritual-" + key + "]");
    if (!child) return "";
    if (key === "image" && child.tagName === "IMG") return child.currentSrc || child.src;
    if (key === "href" && child.tagName === "A") return child.href;
    return child.getAttribute("data-value") || child.textContent.trim();
  };

  AmanaController.prototype.populateRitualDialog = function (state, trigger) {
    var controller = this;
    queryAll(state.dialog, "*").forEach(function (element) {
      toArray(element.attributes).forEach(function (attribute) {
        if (attribute.name.indexOf("data-dialog-") !== 0) return;
        var key = attribute.name.slice("data-dialog-".length);
        if (!key || key === "close" || key === "initial-focus") return;
        var value = controller.ritualSourceValue(trigger, key);
        if (value === undefined) return;

        if (value === "") {
          if (element.tagName === "IMG" && (key === "image" || key === "src")) {
            element.removeAttribute("src");
            element.hidden = true;
          } else if (element.tagName === "A" && (key === "href" || key === "link")) {
            element.removeAttribute("href");
          } else if ("value" in element && /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName)) {
            element.value = "";
          } else {
            element.textContent = "";
          }
          return;
        }

        if (element.tagName === "IMG" && (key === "image" || key === "src")) {
          element.src = value;
          var alt = controller.ritualSourceValue(trigger, "alt") || controller.ritualSourceValue(trigger, "title");
          if (alt) element.alt = alt;
          element.hidden = false;
        } else if (element.tagName === "A" && (key === "href" || key === "link")) {
          element.href = value;
        } else if ("value" in element && /^(INPUT|TEXTAREA|SELECT)$/.test(element.tagName)) {
          element.value = value;
        } else {
          element.textContent = value;
        }
      });
    });
  };

  AmanaController.prototype.setupDialogs = function () {
    var controller = this;
    var ritualStates = queryAll(document, "[data-amana-ritual-dialog]").map(function (dialog) {
      return controller.createDialogState(dialog, "ritual");
    });
    var passwordStates = queryAll(document, "[data-amana-password-dialog]").map(function (dialog) {
      return controller.createDialogState(dialog, "password");
    });

    queryAll(document, "[data-ritual-open]").forEach(function (trigger) {
      addListener(controller.cleanups, trigger, "click", function (event) {
        if (isModifiedClick(event)) return;
        var requested = trigger.getAttribute("data-ritual-open");
        var state = ritualStates.find(function (candidate) {
          return requested && (
            candidate.dialog.id === requested.replace(/^#/, "") ||
            candidate.dialog.getAttribute("data-amana-ritual-dialog") === requested
          );
        }) || ritualStates[0];
        if (!state) return;
        event.preventDefault();
        controller.populateRitualDialog(state, trigger);
        state.openDialog(trigger);
      });
    });

    passwordStates.forEach(function (state) {
      var id = state.dialog.id;
      var selector = [
        "[data-amana-password-open]",
        "[aria-controls='" + cssEscape(id) + "']",
        "a[href='#" + cssEscape(id) + "']"
      ].join(",");
      queryAll(document, selector).forEach(function (trigger) {
        addListener(controller.cleanups, trigger, "click", function (event) {
          if (isModifiedClick(event)) return;
          event.preventDefault();
          state.openDialog(trigger);
        });
      });

      if (state.dialog.querySelector(".amana-form-message--error")) {
        window.requestAnimationFrame(function () {
          if (!controller.destroyed) state.openDialog(null);
        });
      }
    });
  };

  AmanaController.prototype.setupSignup = function () {
    var controller = this;
    queryAll(document, "[data-amana-signup]").forEach(function (signup) {
      var leadForm = signup.querySelector(".amana-signup__lead-form");
      var qualificationForm = signup.querySelector(".amana-signup__qualification-form");
      if (!leadForm || !qualificationForm) return;

      var firstName = leadForm.querySelector("[data-amana-first-name]");
      var email = leadForm.querySelector("[data-amana-email]");
      var tagsInput = leadForm.querySelector("[data-amana-signup-tags]");
      var error = leadForm.querySelector("[data-amana-signup-error]");
      var qualify = signup.querySelector("[data-amana-signup-stage='qualify']");
      var done = signup.querySelector("[data-amana-signup-stage='done']");
      var qualificationEmail = qualificationForm.querySelector("[data-amana-qualification-email]");
      var skip = qualificationForm.querySelector("[data-amana-signup-skip]");
      var customerSuccess = signup.querySelector("[data-amana-customer-success]");
      var qualificationSuccess = signup.querySelector("[data-amana-qualification-success]");
      var stages = queryAll(signup, "[data-amana-signup-stage]");
      var storageKey = "amana-signup-email";

      if (error) {
        ensureId(error, "amana-signup-error");
        if (!error.hasAttribute("data-amana-default-message")) {
          error.setAttribute("data-amana-default-message", error.textContent.trim());
        }
      }
      if (tagsInput && !tagsInput.hasAttribute("data-amana-base-tags")) {
        tagsInput.setAttribute("data-amana-base-tags", tagsInput.value || "");
      }

      function setFieldError(input, invalid) {
        if (!input) return;
        input.setAttribute("aria-invalid", invalid ? "true" : "false");
        if (error) mergeDescribedBy(input, error.id, invalid);
      }

      function validateLead() {
        var nameInvalid = !firstName || !firstName.value.trim();
        var emailValue = email ? email.value.trim() : "";
        var emailInvalid = !email || !emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
        if (email && email.validity && emailValue && email.validity.typeMismatch) emailInvalid = true;

        setFieldError(firstName, nameInvalid);
        setFieldError(email, emailInvalid);
        if (error) {
          var fallback = error.getAttribute("data-amana-default-message") || "Saisissez un prénom et une adresse e-mail valide.";
          if (nameInvalid && !emailInvalid) error.textContent = "Saisissez votre prénom.";
          else if (!nameInvalid && emailInvalid) error.textContent = "Saisissez une adresse e-mail valide.";
          else error.textContent = fallback;
          error.hidden = !(nameInvalid || emailInvalid);
        }
        if (nameInvalid && firstName) firstName.focus();
        else if (emailInvalid && email) email.focus();
        return !nameInvalid && !emailInvalid;
      }

      function syncLeadTags() {
        if (!tagsInput) return;
        var tags = (tagsInput.getAttribute("data-amana-base-tags") || "")
          .split(",")
          .map(function (tag) { return sanitiseTag(tag); })
          .filter(Boolean);
        if (firstName && firstName.value.trim()) {
          tags.push(sanitiseTag("Prénom: " + firstName.value.trim()));
        }
        tagsInput.value = tags.filter(function (tag, index, all) {
          return tag && all.indexOf(tag) === index;
        }).join(", ");
      }

      function showStage(stage, focusTarget) {
        stages.forEach(function (candidate) {
          var active = candidate === stage;
          candidate.hidden = !active;
          candidate.setAttribute("aria-hidden", active ? "false" : "true");
          candidate.classList.toggle("is-active", active);
        });
        if (stage) signup.setAttribute("data-amana-signup-state", stage.getAttribute("data-amana-signup-stage") || "");
        controller.scheduleMeasure();
        if (focusTarget) {
          window.requestAnimationFrame(function () {
            focusTarget.focus({ preventScroll: true });
          });
        }
      }

      function rememberEmail() {
        if (!email || !email.value.trim()) return;
        try {
          window.sessionStorage.setItem(storageKey, email.value.trim());
        } catch (_) {
          // Storage can be unavailable in strict privacy modes; the native form still works.
        }
      }

      function restoreQualificationEmail() {
        if (!qualificationEmail || qualificationEmail.value.trim()) return;
        try {
          qualificationEmail.value = window.sessionStorage.getItem(storageKey) || "";
        } catch (_) {
          // The visible field remains editable when storage is unavailable.
        }
      }

      if (qualificationSuccess && done) {
        signup.setAttribute("data-amana-signup-captured", "true");
        showStage(done, null);
      } else if (customerSuccess && qualify) {
        signup.setAttribute("data-amana-signup-captured", "true");
        restoreQualificationEmail();
        showStage(qualify, null);
      } else {
        stages.forEach(function (stage) {
          stage.setAttribute("aria-hidden", stage.hidden ? "true" : "false");
        });
      }

      if (firstName) {
        addListener(controller.cleanups, firstName, "input", function () {
          if (firstName.value.trim()) setFieldError(firstName, false);
          if (error && email && email.getAttribute("aria-invalid") !== "true") error.hidden = true;
          syncLeadTags();
        });
      }
      if (email) {
        addListener(controller.cleanups, email, "input", function () {
          if (email.value.trim() && !email.validity.typeMismatch) setFieldError(email, false);
          if (error && firstName && firstName.getAttribute("aria-invalid") !== "true") error.hidden = true;
        });
      }

      addListener(controller.cleanups, leadForm, "submit", function (event) {
        if (!validateLead()) {
          event.preventDefault();
          return;
        }
        syncLeadTags();
        rememberEmail();
        // Native submission is deliberate: Shopify wires hCaptcha and verifies the response.
      });

      addListener(controller.cleanups, qualificationForm, "submit", function () {
        restoreQualificationEmail();
        // The optional answers use Shopify's native contact form and its own CAPTCHA token.
      });

      if (skip && done) {
        addListener(controller.cleanups, skip, "click", function (event) {
          event.preventDefault();
          showStage(done, done);
        });
      }

      controller.signupStates.push({
        signup: signup,
        form: leadForm,
        lead: signup.querySelector("[data-amana-signup-stage='lead']"),
        qualify: qualify,
        done: done,
        showStage: showStage,
        syncTags: syncLeadTags
      });
    });
  };
  AmanaController.prototype.setupReveal = function () {
    var revealElements = queryAll(document, "[data-reveal]");
    if (!revealElements.length) return;

    if (!this.motionEnabled || !("IntersectionObserver" in window)) {
      revealElements.forEach(function (element) {
        element.classList.add("is-in");
      });
      html.classList.remove("motion-ready", "amana-motion-ready");
      return;
    }

    var viewportThreshold = window.innerHeight * 0.92;
    var pending = [];
    revealElements.forEach(function (element) {
      var authoredDelay = element.style.getPropertyValue("--amana-reveal-delay");
      if (authoredDelay && !element.style.getPropertyValue("--reveal-delay")) {
        element.style.setProperty("--reveal-delay", authoredDelay);
      }
      var rect = element.getBoundingClientRect();
      if (rect.top <= viewportThreshold && rect.bottom >= 0) element.classList.add("is-in");
      else pending.push(element);
    });

    this.revealObserver = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: "0px 0px -10% 0px",
      threshold: 0.04
    });
    pending.forEach(function (element) {
      this.revealObserver.observe(element);
    }, this);

    // Content is hidden only after the observer and all above-fold states exist.
    html.classList.add("motion-ready", "amana-motion-ready");
  };

  AmanaController.prototype.setupLenis = function () {
    var shopifyDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    var hasEnoughCores = this.hardwareConcurrency > 4;
    var eligible =
      typeof window.Lenis === "function" &&
      this.finePointerQuery.matches &&
      !this.reduced &&
      !this.saveData &&
      hasEnoughCores &&
      this.smoothScrollEnabled &&
      !shopifyDesignMode;

    if (!eligible) return;
    try {
      var lenisLerp = this.motionIntensity === "subtle"
        ? 0.18
        : this.motionIntensity === "expressive"
          ? 0.1
          : 0.14;
      this.lenis = new window.Lenis({
        autoRaf: true,
        lerp: lenisLerp,
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 0.9
      });
      html.classList.add("has-amana-lenis");
      if (typeof this.lenis.on === "function") {
        this.lenis.on("scroll", this.scheduleScrollUpdate);
      }
    } catch (error) {
      this.lenis = null;
      html.classList.remove("has-amana-lenis");
      if (window.console && typeof window.console.warn === "function") {
        window.console.warn("[Amana] Lenis could not be initialised; native scrolling remains active.", error);
      }
    }
  };

  AmanaController.prototype.headerOffset = function () {
    var header = document.querySelector("[data-amana-header]");
    return header ? Math.ceil(header.getBoundingClientRect().height + 12) : 12;
  };

  AmanaController.prototype.scrollToTarget = function (target, updateHash) {
    if (!target) return;
    var offset = -this.headerOffset();
    var targetTop = target.getBoundingClientRect().top + window.scrollY + offset;

    if (this.lenis && typeof this.lenis.scrollTo === "function") {
      this.lenis.scrollTo(target, {
        offset: offset,
        immediate: this.reduced,
        force: true
      });
    } else {
      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: this.reduced ? "auto" : "smooth"
      });
    }

    if (updateHash && target.id && window.location.hash !== "#" + target.id) {
      try {
        window.history.pushState(null, "", "#" + encodeURIComponent(target.id));
      } catch (_error) {
        window.location.hash = target.id;
      }
    }
  };

  AmanaController.prototype.setupAnchors = function () {
    var controller = this;
    queryAll(document, "a[href^='#']").forEach(function (link) {
      addListener(controller.cleanups, link, "click", function (event) {
        if (isModifiedClick(event) || link.hasAttribute("download")) return;
        if (link.target && link.target !== "_self") return;
        if (link.matches("[data-ritual-open], [data-amana-password-open], [role='tab']")) return;
        var hash = link.getAttribute("href");
        var target = getHashTarget(hash);
        if (!target || target.matches("[data-amana-ritual-dialog], [data-amana-password-dialog]")) return;
        event.preventDefault();
        controller.scrollToTarget(target, true);
        if (link.classList.contains("amana-skip-link")) {
          window.requestAnimationFrame(function () {
            if (typeof target.focus === "function") target.focus({ preventScroll: true });
          });
        }
      });
    });
  };

  AmanaController.prototype.resolveRailTarget = function (node) {
    var raw = node.getAttribute("data-amana-target") || node.getAttribute("href") || "";
    if (!raw) return null;
    return getHashTarget(raw.charAt(0) === "#" ? raw : "#" + raw);
  };

  AmanaController.prototype.setupScrollLinkedElements = function () {
    var controller = this;
    this.headers = queryAll(document, "[data-amana-header]");
    this.halos = queryAll(document, "[data-amana-hero-halo]");
    this.methods = queryAll(document, "[data-amana-method]");
    this.rails = queryAll(document, "[data-amana-rail]");

    this.rails.forEach(function (rail) {
      queryAll(rail, "[data-amana-target]").forEach(function (node) {
        var target = controller.resolveRailTarget(node);
        if (!target) return;
        if (!/^(A|BUTTON)$/.test(node.tagName)) {
          node.setAttribute("role", "button");
          if (!node.hasAttribute("tabindex")) node.tabIndex = 0;
        }
        function activate(event) {
          if (event && event.type === "keydown" && event.key !== "Enter" && event.key !== " ") return;
          if (event) event.preventDefault();
          controller.scrollToTarget(target, true);
        }
        addListener(controller.cleanups, node, "click", activate);
        addListener(controller.cleanups, node, "keydown", activate);
      });
    });

    addListener(this.cleanups, window, "scroll", this.scheduleScrollUpdate, { passive: true });
    addListener(this.cleanups, window, "resize", this.scheduleMeasure, { passive: true });
  };

  AmanaController.prototype.scheduleMeasure = function () {
    var controller = this;
    if (this.destroyed || this.measureFrame) return;
    this.measureFrame = window.requestAnimationFrame(function () {
      controller.measureFrame = 0;
      controller.measure();
    });
  };

  AmanaController.prototype.measure = function () {
    if (this.destroyed) return;
    var scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    var viewportHeight = Math.max(1, window.innerHeight);
    var maxScroll = Math.max(1, document.documentElement.scrollHeight - viewportHeight);

    // READ PHASE
    var nextRailMetrics = this.rails.map(function (rail) {
      var fill = rail.querySelector("[data-amana-rail-fill]");
      var drop = rail.querySelector("[data-amana-rail-drop]");
      var nodes = queryAll(rail, "[data-amana-target]").map(function (node) {
        var target = this.resolveRailTarget(node);
        if (!target) return null;
        var targetRect = target.getBoundingClientRect();
        return {
          node: node,
          target: target,
          top: targetRect.top + scrollY
        };
      }, this).filter(Boolean);
      var track = (fill && fill.parentElement) || rail;
      var trackRect = track.getBoundingClientRect();
      return {
        rail: rail,
        fill: fill,
        drop: drop,
        nodes: nodes,
        trackHeight: Math.max(0, trackRect.height)
      };
    }, this);

    var nextMethodMetrics = this.methods.map(function (method) {
      var rect = method.getBoundingClientRect();
      return {
        method: method,
        spine: method.querySelector("[data-amana-method-spine]"),
        top: rect.top + scrollY,
        height: Math.max(1, rect.height)
      };
    });

    var nextHaloMetrics = this.halos.map(function (halo) {
      var section = halo.closest("section, [data-amana-section]") || halo.parentElement;
      var rect = section.getBoundingClientRect();
      return {
        halo: halo,
        botanical: section.querySelector("[data-amana-hero-botanical]"),
        top: rect.top + scrollY,
        height: Math.max(1, rect.height)
      };
    });

    // WRITE PHASE
    nextRailMetrics.forEach(function (metric) {
      metric.nodes.forEach(function (item) {
        var position = clamp(item.top / maxScroll, 0, 1);
        item.position = position;
        item.node.style.setProperty("--amana-node-progress", String(position));
        item.node.style.setProperty("--amana-node-position", (position * 100).toFixed(3) + "%");
      });
    });

    this.railMetrics = nextRailMetrics;
    this.methodMetrics = nextMethodMetrics;
    this.haloMetrics = nextHaloMetrics;
    this.viewportHeight = viewportHeight;
    this.maxScroll = maxScroll;
    this.scheduleScrollUpdate();
  };

  AmanaController.prototype.scheduleScrollUpdate = function () {
    var controller = this;
    if (this.destroyed || this.scrollFrame || document.hidden) return;
    this.scrollFrame = window.requestAnimationFrame(function () {
      controller.scrollFrame = 0;
      controller.updateScrollLinked();
    });
  };

  AmanaController.prototype.updateScrollLinked = function () {
    if (this.destroyed) return;

    // READ PHASE — all geometry was cached by measure().
    var scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    var viewportHeight = this.viewportHeight || Math.max(1, window.innerHeight);
    var maxScroll = this.maxScroll || Math.max(1, document.documentElement.scrollHeight - viewportHeight);
    var progress = clamp(scrollY / maxScroll, 0, 1);
    var activationLine = scrollY + viewportHeight * 0.35;
    var headerScrolled = scrollY > 40;

    var railWrites = this.railMetrics.map(function (metric) {
      var activeIndex = -1;
      metric.nodes.forEach(function (item, index) {
        if (activationLine >= item.top) activeIndex = index;
      });
      return {
        metric: metric,
        activeIndex: activeIndex,
        dropY: progress * metric.trackHeight
      };
    });

    var methodWrites = this.methodMetrics.map(function (metric) {
      return {
        metric: metric,
        progress: clamp(
          (scrollY + viewportHeight * 0.72 - metric.top) / Math.max(1, metric.height * 0.86),
          0,
          1
        )
      };
    });

    var haloWrites = this.haloMetrics.map(function (metric) {
      var localProgress = clamp((scrollY - metric.top) / metric.height, 0, 1);
      return {
        metric: metric,
        y: this.ambientMotionEnabled ? localProgress * 48 * this.motionScale : 0,
        botanicalY: this.ambientMotionEnabled ? localProgress * -24 * this.motionScale : 0
      };
    }, this);

    // WRITE PHASE — transforms, state classes and CSS variables only.
    this.headers.forEach(function (header) {
      if (header.classList.contains("is-scrolled") === headerScrolled) return;
      header.classList.toggle("is-scrolled", headerScrolled);
      header.toggleAttribute("data-scrolled", headerScrolled);
    });

    railWrites.forEach(function (write) {
      var metric = write.metric;
      metric.rail.style.setProperty("--amana-scroll-progress", String(progress));
      if (metric.fill) {
        metric.fill.style.setProperty("--amana-progress", String(progress));
        metric.fill.style.transformOrigin = "top";
        metric.fill.style.transform = "scaleY(" + progress.toFixed(5) + ")";
      }
      if (metric.drop) {
        metric.drop.style.setProperty("--amana-rail-y", write.dropY.toFixed(2) + "px");
        metric.drop.style.transform =
          "translate3d(-50%, " + write.dropY.toFixed(2) + "px, 0) rotate(45deg)";
      }
      metric.nodes.forEach(function (item, index) {
        var passed = progress + 0.002 >= item.position;
        var active = index === write.activeIndex;
        item.node.classList.toggle("is-passed", passed);
        item.node.classList.toggle("is-active", active);
        if (active) item.node.setAttribute("aria-current", "step");
        else item.node.removeAttribute("aria-current");
      });
    });

    methodWrites.forEach(function (write) {
      var metric = write.metric;
      metric.method.style.setProperty("--amana-method-progress", String(write.progress));
      if (metric.spine) {
        metric.spine.style.transformOrigin = "top";
        metric.spine.style.transform = "scaleY(" + write.progress.toFixed(5) + ")";
      }
    });

    haloWrites.forEach(function (write) {
      write.metric.halo.style.setProperty("--amana-halo-y", write.y.toFixed(2) + "px");
      write.metric.halo.style.transform = !this.ambientMotionEnabled
        ? "none"
        : "translate3d(0, " + write.y.toFixed(2) + "px, 0)";
      if (write.metric.botanical) {
        write.metric.botanical.style.transform = !this.ambientMotionEnabled
          ? "none"
          : "translate3d(0, " + write.botanicalY.toFixed(2) + "px, 0)";
      }
    }, this);
  };

  AmanaController.prototype.setupLifecycleObservers = function () {
    var controller = this;
    addListener(this.cleanups, document, "visibilitychange", this.handleVisibility);

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(document.body);
      queryAll(document, "[data-amana-section], [data-amana-method], [data-amana-rail]").forEach(function (element) {
        controller.resizeObserver.observe(element);
      });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        if (!controller.destroyed) controller.scheduleMeasure();
      });
    }

    addListener(this.cleanups, window, "load", this.scheduleMeasure, { once: true });
  };

  AmanaController.prototype.handleVisibility = function () {
    html.classList.toggle("amana-page-hidden", document.hidden);
    if (document.hidden) {
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      if (this.lenis && typeof this.lenis.stop === "function") this.lenis.stop();
    } else {
      if (!this.activeOverlay && this.lenis && typeof this.lenis.start === "function") this.lenis.start();
      this.scheduleMeasure();
    }
  };

  AmanaController.prototype.handleBlockSelect = function (block) {
    if (!block) return;
    if (block.matches && block.matches("[data-reveal]")) block.classList.add("is-in");
    queryAll(block, "[data-reveal]").forEach(function (element) {
      element.classList.add("is-in");
    });

    this.accordions.forEach(function (accordion) {
      var item = accordion.items.find(function (candidate) {
        return candidate.panel.contains(block) || (candidate.item && candidate.item.contains(block));
      });
      if (item) accordion.openItem(item);
    });

    this.tabGroups.forEach(function (group) {
      var index = group.pairs.findIndex(function (pair) {
        return pair.panel.contains(block);
      });
      if (index >= 0) group.select(index, false);
    });

    this.scheduleMeasure();
    if (window.Shopify && window.Shopify.designMode && typeof block.scrollIntoView === "function") {
      window.requestAnimationFrame(function () {
        block.scrollIntoView({ behavior: "auto", block: "center" });
      });
    }
  };

  AmanaController.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;
    this.closeActiveOverlay(false);
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame);
    if (this.lenis) {
      if (typeof this.lenis.off === "function") this.lenis.off("scroll", this.scheduleScrollUpdate);
      if (typeof this.lenis.destroy === "function") this.lenis.destroy();
    }
    this.cleanups.splice(0).forEach(function (cleanup) {
      cleanup();
    });
    html.classList.remove(
      "motion-ready",
      "amana-motion-ready",
      "has-amana-lenis",
      "amana-ambient-motion-off",
      "amana-overlay-open",
      "amana-nav-open",
      "amana-page-hidden"
    );
    queryAll(document, "[data-reveal]").forEach(function (element) {
      element.classList.add("is-in");
    });
  };

  var controller = null;
  var refreshFrame = 0;
  var runtimeCleanups = [];

  function refresh() {
    refreshFrame = 0;
    if (controller) controller.destroy();
    controller = new AmanaController();
  }

  function scheduleRefresh() {
    if (refreshFrame) return;
    refreshFrame = window.requestAnimationFrame(refresh);
  }

  function onSectionMutation() {
    scheduleRefresh();
  }

  function onBlockSelect(event) {
    if (controller) controller.handleBlockSelect(event.target);
  }

  function boot() {
    refresh();
    addMediaListener(runtimeCleanups, window.matchMedia("(prefers-reduced-motion: reduce)"), scheduleRefresh);
    addMediaListener(runtimeCleanups, window.matchMedia("(min-width: 990px) and (pointer: fine)"), scheduleRefresh);
    addListener(runtimeCleanups, document, "shopify:section:load", onSectionMutation);
    addListener(runtimeCleanups, document, "shopify:section:unload", onSectionMutation);
    addListener(runtimeCleanups, document, "shopify:block:select", onBlockSelect);
  }

  var runtime = {
    destroy: function () {
      if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
      refreshFrame = 0;
      if (controller) controller.destroy();
      controller = null;
      runtimeCleanups.splice(0).forEach(function (cleanup) {
        cleanup();
      });
    },
    refresh: scheduleRefresh,
    get controller() {
      return controller;
    }
  };
  window[RUNTIME_KEY] = runtime;

  if (document.readyState === "loading") {
    var onReady = function () {
      document.removeEventListener("DOMContentLoaded", onReady);
      boot();
    };
    document.addEventListener("DOMContentLoaded", onReady);
    runtimeCleanups.push(function () {
      document.removeEventListener("DOMContentLoaded", onReady);
    });
  } else {
    boot();
  }
})();
